import { Table } from 'antd';
import { BankOutlined, MedicineBoxOutlined, TeamOutlined } from '@ant-design/icons';
import PhaseShell, { SectionCard, StatChip } from '../../../components/registry/PhaseShell';
import type { PhasePageProps } from '../../../components/registry/RegistryPhaseWorkspace';
import {
  RegistryEncounterTable,
  SectionEmpty,
  groupBySourceDocument,
  registryDate as date,
  renderDocGroups,
} from '../../../components/registry/registryDocGroups';
import { detailsText } from './detailsText';
import { isHospitalEncounter } from './phases';

/** Faza 3 — Tratament & Chirurgie: decizia terapeutică, consulturile oncologice, internările. */
export default function Faza3Page({ record, status }: PhasePageProps) {
  const treatments = record.treatments ?? [];
  const oncologyConsults = record.encounters.filter((e) => e.type === 'ONCOLOGY_CONSULT');
  const hospitalizations = record.encounters.filter(isHospitalEncounter);

  return (
    <PhaseShell
      title="Faza 3 — Tratament & Chirurgie"
      description="Decizia comisiei multidisciplinare și tratamentul/chirurgia pacientului."
      status={status}
      chips={
        <>
          <StatChip count={treatments.length} label="decizii terapeutice" />
          <StatChip count={oncologyConsults.length} label="consulturi oncologice" />
          <StatChip count={hospitalizations.length} label="internări" />
        </>
      }
    >
      <SectionCard icon={<MedicineBoxOutlined />} title="Decizia terapeutică (comisia multidisciplinară)">
        {treatments.length === 0 && oncologyConsults.length === 0 ? (
          <SectionEmpty />
        ) : (
          <>
            {renderDocGroups(
              groupBySourceDocument(treatments, record.documents, (t) => t.sourceDocumentId),
              (items) => (
                <Table
                  size="small"
                  rowKey={(t) => t.decisionDate}
                  pagination={false}
                  style={{ marginBottom: treatments.length ? 16 : 0 }}
                  dataSource={items}
                  columns={[
                    { title: 'Data deciziei', dataIndex: 'decisionDate', key: 'd', render: (v: string | null) => date(v) },
                    { title: 'Detalii', key: 'det', render: (_, t) => detailsText(t.details) || '—' },
                  ]}
                />
              ),
            )}
            {oncologyConsults.length > 0 && (
              <>
                <div style={{ fontWeight: 600, margin: '8px 0 4px' }}>
                  <TeamOutlined /> Consulturi oncologice
                </div>
                {renderDocGroups(
                  groupBySourceDocument(oncologyConsults, record.documents, (e) => e.sourceDocumentId),
                  (items) => <RegistryEncounterTable encounters={items} />,
                )}
              </>
            )}
          </>
        )}
      </SectionCard>

      <SectionCard icon={<BankOutlined />} title="Internări și proceduri">
        {hospitalizations.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(hospitalizations, record.documents, (e) => e.sourceDocumentId),
            (items) => <RegistryEncounterTable encounters={items} showProcedures />,
          )
        )}
      </SectionCard>
    </PhaseShell>
  );
}
